import {makeTrackEvent} from './makeTrackEvent';

export type MarimoNotebookAction =
  | 'created'
  | 'deleted'
  | 'opened'
  | 'opened_in_artifacts'
  | 'renamed';

export type MarimoNotebookLocation =
  | 'forge_page'
  | 'notebook_page'
  | 'project_page'
  | 'team_page';

export type MarimoNotebookSource =
  | {
      notebook_source_id: null;
      notebook_source_type: 'blank';
    }
  | {
      notebook_source_id: string;
      notebook_source_type: 'duplicate';
    }
  | {
      notebook_source_id: string;
      notebook_source_type: 'quickstart';
    };

type MarimoNotebookEvent = {
  location: MarimoNotebookLocation;
  notebook_id: string;
};

export type MarimoNotebookInteractionEvent =
  | (MarimoNotebookEvent & MarimoNotebookSource & {action: 'created'})
  | (MarimoNotebookEvent & {
      action: Exclude<MarimoNotebookAction, 'created'>;
    });

export const MARIMO_NOTEBOOK_BLANK_SOURCE = {
  notebook_source_id: null,
  notebook_source_type: 'blank',
} satisfies MarimoNotebookSource;

type AnnotatedMarimoNotebookInteractionEvent = {
  action: MarimoNotebookAction;
  location: MarimoNotebookLocation;
  notebook_id: string;
  notebook_source_id: string | null;
  notebook_source_type: MarimoNotebookSource['notebook_source_type'] | null;
};

const trackMarimoNotebookInteractionEvent = makeTrackEvent<
  AnnotatedMarimoNotebookInteractionEvent,
  {
    action: {
      description: 'the notebook interaction that completed';
      exampleValues: [
        'created',
        'deleted',
        'opened',
        'opened_in_artifacts',
        'renamed',
      ];
    };
    location: {
      description: 'the page where the interaction started';
      exampleValues: [
        'forge_page',
        'notebook_page',
        'project_page',
        'team_page',
      ];
    };
    notebook_id: {
      description: 'the interacted-with notebook, or the new notebook for created events';
    };
    notebook_source_id: {
      description: 'the quickstart name or duplicated notebook id, or null when there is no source';
    };
    notebook_source_type: {
      description: 'how a notebook was created, or null for non-created actions';
      exampleValues: ['blank', 'duplicate', 'quickstart', null];
    };
    _description: 'Tracks creation and management interactions with Marimo notebooks';
    _location: 'Project notebooks, team notebooks, Forge notebooks, and the notebook editor page';
    _motivation: 'Measure how notebooks are created, opened, and managed across their entry points';
  }
>('Marimo notebooks interaction');

export const trackMarimoNotebookInteraction = (
  event: MarimoNotebookInteractionEvent
): void => {
  if (event.action === 'created') {
    trackMarimoNotebookInteractionEvent(event);
    return;
  }

  trackMarimoNotebookInteractionEvent({
    ...event,
    notebook_source_id: null,
    notebook_source_type: null,
  });
};
